// src/layouts/WorkspaceLayout.tsx
// Unified workspace layout providing view switching, filtering, density, search,
// grouping, and sorting controls. Manages WorkspaceState via URL search params.

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';

import { FilterBar } from '@/components/FilterBar';
import { GroupBySelect } from '@/components/GroupBySelect';
import { SortBySelect } from '@/components/SortBySelect';
import { DensityToggle } from '@/components/DensityToggle';

import type {
  DensityMode,
  FilterValue,
  SortDirection,
  ViewType,
  WorkspaceConfig,
  WorkspaceState,
} from '@/features/workspace/types';

import {
  buildDefaults,
  parseWorkspaceStateFromUrl,
  serializeWorkspaceStateToUrl,
  setView,
  setGroupBy,
  setSortBy,
  setDensity,
  setSearch,
  addFilter,
  removeFilter,
  clearFilters,
  getAvailableGroups,
} from '@/features/workspace/logic';

// ---------------------------------------------------------------------------
// Workspace context
// ---------------------------------------------------------------------------

/** Delay before a keystroke in the search box turns into a request. */
export const SEARCH_DEBOUNCE_MS = 300;

export type WorkspaceAction =
  | { type: 'SET_VIEW'; view: ViewType }
  | { type: 'SET_GROUP_BY'; groupBy: string | null }
  | { type: 'SET_SORT_BY'; field: string; direction?: 'asc' | 'desc' }
  | { type: 'SET_DENSITY'; density: DensityMode }
  | { type: 'SET_SEARCH'; search: string }
  | { type: 'ADD_FILTER'; filter: FilterValue }
  | { type: 'REMOVE_FILTER'; field: string }
  | { type: 'CLEAR_FILTERS' }
  /**
   * Replace the whole state at once. Applying a saved view moves the view
   * type, every filter, the search, the sort and the grouping together; doing
   * that as a sequence of single-field actions would write the URL — and fire
   * a request — once per field.
   */
  | { type: 'SET_STATE'; state: WorkspaceState };

interface WorkspaceContextValue {
  state: WorkspaceState;
  /** The state an untouched workspace starts from, derived from `config`. */
  defaults: WorkspaceState;
  config: WorkspaceConfig;
  dispatch: (action: WorkspaceAction) => void;
}

const WorkspaceCtx = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a <WorkspaceLayout>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// URL subscription
// ---------------------------------------------------------------------------

function subscribeToSearchParams(callback: () => void): () => void {
  window.addEventListener('popstate', callback);
  return () => window.removeEventListener('popstate', callback);
}

let _cachedSearch = '';
let _cachedParams = new URLSearchParams('');

function getSearchParamsSnapshot(): URLSearchParams {
  const current = window.location.search;
  if (current !== _cachedSearch) {
    _cachedSearch = current;
    _cachedParams = new URLSearchParams(current);
  }
  return _cachedParams;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function applyStateToUrl(state: WorkspaceState, defaults: WorkspaceState) {
  const params = serializeWorkspaceStateToUrl(state, defaults);

  // Preserve non-workspace params (scope, etc.)
  const current = new URLSearchParams(window.location.search);
  const workspaceKeys = new Set(['view', 'filter', 'group', 'sort', 'dir', 'density', 'q', 'saved']);
  const preserved = new URLSearchParams();
  for (const [key, value] of current.entries()) {
    if (!workspaceKeys.has(key)) {
      preserved.append(key, value);
    }
  }
  for (const [key, value] of params.entries()) {
    preserved.append(key, value);
  }

  const qs = preserved.toString();
  const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  window.history.replaceState(window.history.state, '', newUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ---------------------------------------------------------------------------
// View icons (SVG paths)
// ---------------------------------------------------------------------------

const VIEW_ICONS: Record<string, string> = {
  list: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  board: 'M9 4h6v16H9zM3 4h6v10H3zM15 4h6v12h-6z',
  timeline: 'M3 12h4l3-9 4 18 3-9h4',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  schedule: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
};

// ---------------------------------------------------------------------------
// Layout component
// ---------------------------------------------------------------------------

interface WorkspaceLayoutProps {
  config: WorkspaceConfig;
  title: string;
  createLabel?: string;
  onCreateClick?: () => void;
  /** Rendered at the start of the toolbar row — e.g. a <ScopeSwitcher />. */
  toolbarStart?: ReactNode;
  /**
   * Rendered on its own row above the toolbar — the saved-views strip. It is a
   * slot rather than a built-in because saved views are a per-product resource
   * (task views, document views); the layout only owns the space they sit in.
   */
  viewsStrip?: ReactNode;
  children: ReactNode;
}

export const WorkspaceLayout = observer(({
  config,
  title,
  createLabel,
  onCreateClick,
  toolbarStart,
  viewsStrip,
  children,
}: WorkspaceLayoutProps) => {
  const { t } = useI18n();

  // Read state from URL
  const searchParams = useSyncExternalStore(subscribeToSearchParams, getSearchParamsSnapshot);

  const defaults = useMemo<WorkspaceState>(() => buildDefaults(config), [config]);

  const state = useMemo(
    () => parseWorkspaceStateFromUrl(searchParams, config),
    [searchParams, config],
  );

  const dispatch = useCallback(
    (action: WorkspaceAction) => {
      let next: WorkspaceState;
      switch (action.type) {
        case 'SET_VIEW':
          next = setView(state, action.view);
          break;
        case 'SET_GROUP_BY':
          next = setGroupBy(state, action.groupBy);
          break;
        case 'SET_SORT_BY':
          next = setSortBy(state, action.field, action.direction);
          break;
        case 'SET_DENSITY':
          next = setDensity(state, action.density);
          break;
        case 'SET_SEARCH':
          next = setSearch(state, action.search);
          break;
        case 'ADD_FILTER':
          next = addFilter(state, action.filter);
          break;
        case 'REMOVE_FILTER':
          next = removeFilter(state, action.field);
          break;
        case 'CLEAR_FILTERS':
          next = clearFilters(state);
          break;
        case 'SET_STATE':
          next = action.state;
          break;
        default:
          return;
      }
      applyStateToUrl(next, defaults);
    },
    [state, defaults],
  );

  const contextValue = useMemo<WorkspaceContextValue>(
    () => ({ state, defaults, config, dispatch }),
    [state, defaults, config, dispatch],
  );

  const availableGroups = useMemo(
    () => getAvailableGroups(config, state.view),
    [config, state.view],
  );

  // ── Debounced search ──────────────────────────────────────────────────
  // The input stays fully responsive while only one request is issued per
  // pause in typing, instead of one per keystroke.
  const [searchDraft, setSearchDraft] = useState(state.search);
  const committedSearch = useRef(state.search);

  // Adopt search changes that came from outside the input (back/forward,
  // a shared URL, a programmatic clear-filters).
  useEffect(() => {
    if (state.search !== committedSearch.current) {
      committedSearch.current = state.search;
      setSearchDraft(state.search);
    }
  }, [state.search]);

  useEffect(() => {
    if (searchDraft === committedSearch.current) return;
    const timer = window.setTimeout(() => {
      committedSearch.current = searchDraft;
      dispatch({ type: 'SET_SEARCH', search: searchDraft });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, dispatch]);

  const handleAddFilter = useCallback(
    (filter: FilterValue) => dispatch({ type: 'ADD_FILTER', filter }),
    [dispatch],
  );

  const handleRemoveFilter = useCallback(
    (field: string) => dispatch({ type: 'REMOVE_FILTER', field }),
    [dispatch],
  );

  const handleClearFilters = useCallback(
    () => dispatch({ type: 'CLEAR_FILTERS' }),
    [dispatch],
  );

  const handleGroupChange = useCallback(
    (groupBy: string | null) => dispatch({ type: 'SET_GROUP_BY', groupBy }),
    [dispatch],
  );

  const handleSortChange = useCallback(
    (field: string, direction?: SortDirection) => dispatch({ type: 'SET_SORT_BY', field, direction }),
    [dispatch],
  );

  const handleDensityChange = useCallback(
    (density: DensityMode) => dispatch({ type: 'SET_DENSITY', density }),
    [dispatch],
  );

  return (
    <WorkspaceCtx.Provider value={contextValue}>
      <div className="space-y-4">
        {/* Header row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-foreground">{t(title)}</h1>

          <div className="flex min-w-0 items-center gap-2 flex-wrap">
            {/* View switcher. It scrolls rather than clipping: the quick-create
                drawer takes 400px out of this row, and a clipped tab is a view
                the person cannot reach at all. */}
            <div className="flex max-w-full items-center overflow-x-auto rounded-lg border border-border bg-muted/30 p-0.5" role="tablist">
              {config.availableViews.filter(v => v.available).map(v => (
                <button
                  key={v.id}
                  role="tab"
                  onClick={() => dispatch({ type: 'SET_VIEW', view: v.id })}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    state.view === v.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={t(v.labelKey)}
                  aria-label={t(v.labelKey)}
                  aria-selected={state.view === v.id}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={VIEW_ICONS[v.icon] || VIEW_ICONS.list} />
                  </svg>
                  <span className="hidden md:inline">{t(v.labelKey)}</span>
                </button>
              ))}
            </div>

            {/* Create button */}
            {onCreateClick && createLabel && (
              <Button size="sm" onClick={onCreateClick}>
                {t(createLabel)}
              </Button>
            )}
          </div>
        </div>

        {/* Saved views strip */}
        {viewsStrip}

        {/* Toolbar row: scope + search + filter chips on the left,
            grouping / sorting / density on the right */}
        {/* Wraps on running out of room rather than at a viewport breakpoint:
            the quick-create drawer narrows this container while the window
            stays wide, and a `lg:` rule cannot see that. */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 basis-64 flex-wrap items-center gap-2">
            {toolbarStart}
            <FilterBar
              filters={state.filters}
              availableFilters={config.availableFilters}
              onAddFilter={handleAddFilter}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearFilters}
              search={searchDraft}
              onSearchChange={setSearchDraft}
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {availableGroups.length > 0 && (
              <GroupBySelect
                groups={availableGroups}
                activeGroup={state.groupBy}
                onGroupChange={handleGroupChange}
              />
            )}

            {config.availableSorts.length > 0 && (
              <SortBySelect
                sorts={config.availableSorts}
                activeSort={state.sortBy}
                direction={state.sortDirection}
                onSortChange={handleSortChange}
              />
            )}

            <DensityToggle density={state.density} onDensityChange={handleDensityChange} />
          </div>
        </div>

        {/* View content */}
        <div role="tabpanel">
          {children}
        </div>
      </div>
    </WorkspaceCtx.Provider>
  );
});
